import React, { useState } from 'react';
import { Task } from '../types/task';
import { updateTask } from '../api/tasks';
import Button from './Button';

interface OverdueTaskModalProps {
    task: Task;
    daysOverdue: number;
    onSuccess: (updatedTask: Task) => void;
    onClose: () => void;
}

const OverdueTaskModal: React.FC<OverdueTaskModalProps> = ({ task, daysOverdue, onSuccess, onClose }) => {
    const [newDueDate, setNewDueDate] = useState<string>(task.due_date || '');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const handleReschedule = async () => {
        if (!newDueDate) {
            setError('Please select a new due date');
            return;
        }

        const selectedDate = new Date(newDueDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate < today) {
            setError('New due date cannot be in the past');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const updatedTask = await updateTask(String(task.id), { due_date: newDueDate });
            onSuccess(updatedTask);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update task';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-2xl max-w-[500px] w-full max-h-[90vh] overflow-y-auto">
                <div className="mb-4 pb-4 border-b-2 border-gray-100 dark:border-gray-700">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 m-0">⚠️ Reschedule Overdue Task</h3>
                </div>

                <div className="text-gray-700 dark:text-gray-300">
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg text-red-900 dark:text-red-100 mb-6">
                        <strong>Warning:</strong> This task is {daysOverdue} day{daysOverdue > 1 ? 's' : ''} overdue!
                        <div className="mt-2 text-sm">
                            Current due date: <strong>{task.due_date ? new Date(task.due_date).toLocaleDateString() : 'Not set'}</strong>
                        </div>
                    </div>

                    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-6">
                        <h4 className="text-base font-semibold mb-2 text-gray-900 dark:text-gray-100">
                            {task.title}
                        </h4>
                        {task.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                                {task.description}
                            </p>
                        )}
                        <p className="text-sm text-gray-600 dark:text-gray-400 m-0">
                            <strong>Status:</strong> {task.status.replace('_', ' ')} | <strong>Priority:</strong> {task.priority}
                        </p>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            New Due Date: <span className="text-red-600 dark:text-red-400">*</span>
                        </label>
                        <input
                            type="date"
                            value={newDueDate}
                            onChange={(e) => setNewDueDate(e.target.value)}
                            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                            min={new Date().toISOString().split('T')[0]}
                        />
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Select a future date to reschedule this task
                        </div>
                    </div>

                    {error && (
                        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg text-red-900 dark:text-red-100">
                            {error}
                        </div>
                    )}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700 flex gap-2 flex-wrap">
                    <Button 
                        onClick={handleReschedule}
                        variant="primary"
                        disabled={loading}
                    >
                        {loading ? 'Rescheduling...' : 'Reschedule Task'}
                    </Button>
                    <Button 
                        onClick={onClose}
                        variant="outline"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default OverdueTaskModal;
